import ReactGA from 'react-ga4';
import type { AppTourStep } from 'Components/AppTour';
import { ApptourFeature, ApptourFeatureItem } from 'Utils/AppTourFeatures';

/**
 * The tour for players the app cannot hear (any input but a MIDI keyboard).
 *
 * Sight reading shows a short list instead of the graded tour: the play button,
 * playing along, "Not graded" on the blank accuracy pill and tempo. The last two
 * only exist in this tour, so each is tracked as seen under its own feature key
 * (the per-user, per-feature rows behind get-unseen-features / set-has-seen):
 * - a player who has never had a sight reading tour sees the whole list once;
 * - a player who already finished the graded tour (lesson_tutorial_seenat is
 *   set) sees only the keyed tips they have not seen.
 * This tour never sets lesson_tutorial_seenat, so a player who later connects
 * a keyboard still gets the whole graded tour.
 *
 * Tiers is shown once ever, the first time a level with more than one tier is
 * open: appended to the tour when one opens then, otherwise on its own.
 *
 * A key the server does not know is never listed as unseen, so its tip counts
 * as seen and is never sent to set-has-seen (the server refuses a request that
 * names an unknown key). Without the server's keys the tour therefore stays
 * closed rather than coming back on every visit.
 *
 * Features of the graded tour (adaptive tempo, for one) are ignored here: this
 * tour neither waits on them nor marks them seen.
 */

/** A sight reading tip of this tour. */
export type UngradedLessonTip = 'play_button' | 'play_along' | 'not_graded' | 'tempo' | 'tiers';

/** The tip on the blank accuracy pill (sight reading) and the greyed accuracy control (repertoire). */
export const NOT_GRADED_TITLE = 'Not graded';
export const NOT_GRADED_TEXT =
  "MuseFlow isn't grading your notes right now. Connect a MIDI keyboard to track your progress.";

/** The sight reading tour, in order (Tiers joins it only when due). */
export const UNGRADED_LESSON_TOUR: readonly UngradedLessonTip[] = [
  'play_button',
  'play_along',
  'not_graded',
  'tempo',
];

/** The feature key each tip only this tour has is tracked under. */
export const UNGRADED_TIP_FEATURE: Readonly<Partial<Record<UngradedLessonTip, ApptourFeature>>> = {
  not_graded: ApptourFeature.UNGRADED_ACCURACY,
  tempo: ApptourFeature.UNGRADED_TEMPO,
  tiers: ApptourFeature.UNGRADED_TIERS,
};

const isListedUnseen = (
  feature: ApptourFeature,
  unseenFeatures: readonly ApptourFeatureItem[],
): boolean => unseenFeatures.some((unseen) => unseen.key === feature);

const levelHasTiers = (levelTierCount: number | null): boolean =>
  levelTierCount !== null && levelTierCount > 1;

export interface UngradedLessonTourInputs {
  /** lesson_tutorial_seenat is set: the graded tour was finished or skipped. */
  gradedTourSeen: boolean;
  /** The server's unseen feature list. */
  unseenFeatures: readonly ApptourFeatureItem[];
  /** The open level's tier count, null while not known. */
  levelTierCount: number | null;
  /** Tips already shown in this app session (the unseen list refreshes after a delay). */
  shownThisSession: ReadonlySet<UngradedLessonTip>;
}

/** The sight reading tips due on their own now, in tour order; empty when none is. */
export function ungradedLessonTipsDue({
  gradedTourSeen,
  unseenFeatures,
  levelTierCount,
  shownThisSession,
}: UngradedLessonTourInputs): UngradedLessonTip[] {
  const isDue = (tip: UngradedLessonTip): boolean => {
    const feature = UNGRADED_TIP_FEATURE[tip];
    return (
      feature !== undefined && !shownThisSession.has(tip) && isListedUnseen(feature, unseenFeatures)
    );
  };
  const tiers: UngradedLessonTip[] =
    levelHasTiers(levelTierCount) && isDue('tiers') ? ['tiers'] : [];
  const keyedTips = UNGRADED_LESSON_TOUR.filter(isDue);
  if (keyedTips.length === 0) return tiers;
  const firstTour = gradedTourSeen
    ? keyedTips
    : UNGRADED_LESSON_TOUR.filter((tip) => !UNGRADED_TIP_FEATURE[tip] || keyedTips.includes(tip));
  return [...firstTour, ...tiers];
}

/** What "Game Tutorial" replays without a heard input: the whole list, with Tiers on a level that has tiers. */
export const ungradedLessonTipsToReplay = (levelTierCount: number | null): UngradedLessonTip[] => [
  ...UNGRADED_LESSON_TOUR,
  ...(levelHasTiers(levelTierCount) ? (['tiers'] as const) : []),
];

/**
 * The feature keys to mark seen once a tour with these steps opens: the keys
 * the server lists as unseen, and no other (an unknown key would make it
 * refuse the whole request).
 */
export const featuresToMarkSeen = (
  steps: readonly AppTourStep[],
  unseenFeatures: readonly ApptourFeatureItem[],
): ApptourFeature[] =>
  steps
    .map((step) => step.feature)
    .filter(
      (feature): feature is ApptourFeature =>
        feature !== undefined && isListedUnseen(feature, unseenFeatures),
    );

/** The repertoire step the tour without a heard input replaces. */
export const GRADED_REPERTOIRE_ACCURACY_TARGET = 'rep-accuracy-dropdown';

/**
 * The repertoire tour without a heard input: the graded tour with its accuracy
 * step (found by target: its number depends on whether the starting tempo
 * step is shown) replaced by the tip on the greyed accuracy control.
 */
export const withUngradedRepertoireAccuracy = (
  gradedSteps: readonly AppTourStep[],
  notGradedStep: AppTourStep,
): AppTourStep[] =>
  gradedSteps.map((step) =>
    step.targetId === GRADED_REPERTOIRE_ACCURACY_TARGET ? notGradedStep : step,
  );

/** Which repertoire tour opens on its own without a heard input. */
export type UngradedRepertoireTourDue = 'whole' | 'tip' | 'none';

/**
 * Repertoire opens its tour on its own while the "Not graded" tip is unseen:
 * the whole tour for a player who has never had it, only the tip for one who
 * has (repertoire_tutorial_seenat is set).
 */
export const ungradedRepertoireTourDue = ({
  repertoireTourSeen,
  unseenFeatures,
  shownThisSession,
}: {
  repertoireTourSeen: boolean;
  unseenFeatures: readonly ApptourFeatureItem[];
  shownThisSession: boolean;
}): UngradedRepertoireTourDue => {
  if (
    shownThisSession ||
    !isListedUnseen(ApptourFeature.UNGRADED_REPERTOIRE_ACCURACY, unseenFeatures)
  ) {
    return 'none';
  }
  return repertoireTourSeen ? 'tip' : 'whole';
};

/** Where a tour runs, for analytics. */
export type TourSurface = 'sight_reading' | 'repertoire';

/** A tour step's analytics key: its own key, or its title in snake case. */
export const tourStepKey = (step: AppTourStep): string =>
  step.stepKey ??
  step.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export interface TooltipSeen {
  surface: TourSurface;
  graded: boolean;
  step: AppTourStep;
  stepNumber: number;
  stepCount: number;
}

/** Sends `tooltip_seen` to Google Analytics for a tour step on screen. */
export function reportTooltipSeen({
  surface,
  graded,
  step,
  stepNumber,
  stepCount,
}: TooltipSeen): void {
  ReactGA.event('tooltip_seen', {
    step_key: tourStepKey(step),
    grading: graded ? 'graded' : 'ungraded',
    surface,
    step_number: stepNumber,
    step_count: stepCount,
  });
}
